// Small hand-drawn inline SVG line chart: 12 monthly values for a PLZ and for the reference.
// No charting library, per docs/DESIGN.md §6.2.

export interface MonthlyChartOptions {
  width?: number;
  height?: number;
  unit: string;
  plzLabel: string;
  refLabel: string;
}

const PAD = { top: 10, right: 10, bottom: 18, left: 30 };

/** Build an SVG path `d` string from 12 values, breaking the line at null (missing) months. */
function buildPath(values: (number | null)[], x: (i: number) => number, y: (v: number) => number): string {
  let d = "";
  let open = false;
  values.forEach((v, i) => {
    if (v === null) {
      open = false;
      return;
    }
    const cmd = open ? "L" : "M";
    d += `${cmd}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
    open = true;
  });
  return d.trim();
}

/** Returns an SVG markup string (caller sets it via innerHTML into a container). */
export function renderMonthlyChart(
  plzValues: (number | null)[],
  refValues: (number | null)[],
  opts: MonthlyChartOptions,
): string {
  const width = opts.width ?? 280;
  const height = opts.height ?? 130;
  const innerW = width - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;

  const all = [...plzValues, ...refValues].filter((v): v is number => v !== null && Number.isFinite(v));
  let lo = all.length ? Math.min(...all) : 0;
  let hi = all.length ? Math.max(...all) : 1;
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }
  const pad = (hi - lo) * 0.08;
  lo -= pad;
  hi += pad;

  const x = (i: number) => PAD.left + (innerW * i) / 11;
  const y = (v: number) => PAD.top + innerH - ((v - lo) / (hi - lo)) * innerH;

  const zeroLine = lo < 0 && hi > 0 ? `<line x1="${PAD.left}" y1="${y(0).toFixed(1)}" x2="${(width - PAD.right).toFixed(1)}" y2="${y(0).toFixed(1)}" class="chart-zero" />` : "";

  const plzPath = buildPath(plzValues, x, y);
  const refPath = buildPath(refValues, x, y);

  const yTicks = [hi, (hi + lo) / 2, lo]
    .map((v) => `<text x="${(PAD.left - 6).toFixed(1)}" y="${(y(v) + 3).toFixed(1)}" class="chart-tick" text-anchor="end">${v.toFixed(1)}</text>`)
    .join("");

  return `
    <svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Monthly ${opts.unit} for ${opts.plzLabel} and ${opts.refLabel}">
      ${zeroLine}
      ${yTicks}
      <path d="${refPath}" class="chart-line chart-line-ref" fill="none" />
      <path d="${plzPath}" class="chart-line chart-line-plz" fill="none" />
    </svg>`;
}
